<script setup>
/**
 * Where tags are made and unmade. Assigning them happens on the transaction
 * itself; this is the list behind that — one place to see what exists, how much
 * it is used, and to get rid of what is not.
 */
import { computed, ref } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import { setFilter, tagUsage } from '../../stores'
import { state, closeSheet, toast } from '../../stores/state'
import { t } from '../../stores/i18n'
import { addTag, deleteTag, findTag, renameTag } from '../../stores/tags'

const draft = ref('')
const busy = ref(false)
const error = ref('')
const confirming = ref(null)
const editing = ref(null)
const editText = ref('')

const tags = computed(() => state.tags)
const used = (id) => tagUsage.value.get(id) ?? 0

async function create() {
  const name = draft.value.trim()
  error.value = ''
  if (!name) return
  if (findTag(name)) {
    error.value = t('common.exists', { name })
    return
  }
  busy.value = true
  await addTag(name)
  busy.value = false
  draft.value = ''
  toast(t('tagSheet.created'), 'good')
}

function startEdit(tag) {
  editing.value = tag.id
  editText.value = tag.name
  confirming.value = null
}

async function commitEdit(tag) {
  const name = editText.value.trim()
  editing.value = null
  if (!name || name === tag.name) return
  const clash = findTag(name)
  if (clash && clash.id !== tag.id) {
    error.value = t('common.exists', { name })
    return
  }
  await renameTag(tag.id, name)
}

/** Deleting a tag removes it from every transaction that wore it. */
async function remove(tag) {
  if (confirming.value !== tag.id) {
    confirming.value = tag.id
    return
  }
  busy.value = true
  await deleteTag(tag.id)
  busy.value = false
  confirming.value = null
  toast(t('tagSheet.deleted'))
}

function focusTag(tag) {
  setFilter('tags', [tag.id])
  state.ui.panel = 'transactions'
  closeSheet()
}
</script>

<template>
  <Sheet :title="t('charts.tags')" :subtitle="t('tagSheet.subtitle')" @close="closeSheet">
    <div class="grid gap-4">
      <form class="flex items-end gap-2" @submit.prevent="create">
        <div class="min-w-0 flex-1">
          <label class="label" for="tag-new">{{ t('tagSheet.newTag') }}</label>
          <input
            id="tag-new"
            v-model="draft"
            class="input"
            :placeholder="t('tagSheet.placeholder')"
            maxlength="40"
            @input="error = ''"
          />
        </div>
        <button class="btn-primary" type="submit" :disabled="!draft.trim() || busy">
          <Icon name="plus" :size="16" /> {{ t('common.add') }}
        </button>
      </form>

      <p v-if="error" class="-mt-2 text-[12.5px] text-danger">{{ error }}</p>

      <ul v-if="tags.length" class="grid gap-1.5">
        <li
          v-for="tag in tags"
          :key="tag.id"
          class="flex items-center gap-2 rounded-xl border border-line-soft bg-raised/50 py-2 pr-2 pl-3"
        >
          <input
            v-if="editing === tag.id"
            v-model="editText"
            class="input !py-1.5 !text-[14px]"
            maxlength="40"
            @keydown.enter.prevent="commitEdit(tag)"
            @blur="commitEdit(tag)"
          />
          <template v-else>
            <button class="min-w-0 flex-1 truncate text-left text-[14px] font-medium" @click="focusTag(tag)">
              {{ tag.name }}
            </button>
            <span class="tnum shrink-0 text-[12px] text-faint">
              {{ used(tag.id) }}
            </span>
            <button class="btn-ghost !px-2 !py-1" :aria-label="t('common.renameThing', { name: tag.name })" @click="startEdit(tag)">
              <Icon name="pencil" :size="15" />
            </button>
            <button
              class="btn-ghost !px-2 !py-1 text-danger"
              :aria-label="t('common.deleteThing', { name: tag.name })"
              @click="remove(tag)"
            >
              <Icon v-if="confirming !== tag.id" name="trash" :size="15" />
              <span v-else class="text-[12px] font-semibold">{{ t('common.sure') }}</span>
            </button>
          </template>
        </li>
      </ul>

      <p v-else class="rounded-xl border border-line-soft bg-raised/50 p-4 text-center text-[13px] text-muted">
        {{ t('tagSheet.empty') }}
      </p>

      <p class="text-[12px] text-faint">{{ t('tagSheet.hint') }}</p>
    </div>

    <template #footer>
      <div class="flex-1" />
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" @click="closeSheet">{{ t('common.done') }}<Kbd combo="mod+enter" /></button>
    </template>
  </Sheet>
</template>
